import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import useOrgs from '@/src/Orgs/components/hooks/useOrgs';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import AreaShell from '../../chrome/AreaShell';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import './org.css';

/*
 * Org stats: year-scale totals from GET /orgs/:orgId/stats. Photos and
 * shares bucket by the activation's year, not the upload date.
 */

interface YearCount {
    year: number;
    count: number;
}

interface OrgStats {
    activationsByYear: YearCount[];
    mediaByYear: YearCount[];
    sharesByYear: YearCount[];
}

const countFor = (rows: YearCount[] | undefined, year: number) =>
    rows?.find((r) => Number(r.year) === year)?.count ?? 0;

function YearBars({ years, rows }: { years: number[]; rows: YearCount[] }) {
    const W = 600;
    const H = 96;
    const ordered = [...years].sort((a, b) => a - b);
    const values = ordered.map((y) => Number(countFor(rows, y)) || 0);
    const max = Math.max(1, ...values);
    const slot = W / ordered.length;
    const bar = Math.min(64, slot * 0.6);
    return (
        <>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Photos by year">
                <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="var(--border-tertiary)" strokeWidth="1" />
                {ordered.map((y, i) => {
                    const h = Math.max(2, (values[i] / max) * (H - 6));
                    return (
                        <rect
                            key={y}
                            x={i * slot + (slot - bar) / 2}
                            y={H - h}
                            width={bar}
                            height={h}
                            rx="1.5"
                            fill="var(--text-secondary)"
                            opacity={values[i] > 0 ? 1 : 0.25}
                        >
                            <title>{`${y} · ${values[i].toLocaleString()} photos`}</title>
                        </rect>
                    );
                })}
            </svg>
            <div className="g-x">
                <span>{ordered[0]}</span>
                <span>{ordered[ordered.length - 1]}</span>
            </div>
        </>
    );
}

export default function OrgStatsV3() {
    const { orgId } = useUserOrg();
    const { getOrgStats } = useOrgs();
    const [stats, setStats] = useState<OrgStats | null>(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!orgId) return;
        let mounted = true;
        setStats(null);
        setFailed(false);
        getOrgStats(orgId)
            .then((data: OrgStats) => {
                if (!mounted) return;
                setStats({
                    activationsByYear: data?.activationsByYear ?? [],
                    mediaByYear: data?.mediaByYear ?? [],
                    sharesByYear: data?.sharesByYear ?? [],
                });
            })
            .catch(() => mounted && setFailed(true));
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, attempt]);

    const retry = useCallback(() => setAttempt((a) => a + 1), []);
    const thisYear = moment().year();

    const years = useMemo(() => {
        if (!stats) return [];
        const all = new Set<number>(
            [...stats.activationsByYear, ...stats.mediaByYear, ...stats.sharesByYear].map((r) => Number(r.year)),
        );
        return Array.from(all)
            .filter((y) => Number.isFinite(y))
            .sort((a, b) => b - a);
    }, [stats]);

    const tiles = stats
        ? [
              { label: 'Activations', value: countFor(stats.activationsByYear, thisYear) },
              { label: 'Photos', value: countFor(stats.mediaByYear, thisYear) },
              { label: 'Shares', value: countFor(stats.sharesByYear, thisYear) },
          ]
        : [];

    return (
        <AreaShell area="org" tab="stats" sub={`Org-wide totals · ${thisYear} so far`}>
            <div className="org-pg">
                {failed ? (
                    <TCard>
                        <TEmpty message="Couldn’t load org stats." actionLabel="Retry" onAction={retry} />
                    </TCard>
                ) : !stats ? (
                    <TCard>
                        <TSkeleton rows={4} />
                    </TCard>
                ) : (
                    <>
                        <div className="org-tiles">
                            {tiles.map((t) => (
                                <div className="tile" key={t.label}>
                                    <p className="tl">
                                        {t.label} in {thisYear}
                                    </p>
                                    <div className="tn">{Number(t.value).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>

                        {years.length > 1 && (
                            <div className="graph">
                                <div className="g-h">
                                    <span className="g-leg">
                                        <span className="sw" style={{ background: 'var(--text-secondary)' }} />
                                        Photos
                                    </span>
                                    <span className="g-when">by year</span>
                                </div>
                                <YearBars years={years} rows={stats.mediaByYear} />
                            </div>
                        )}

                        <div className="sec-h">
                            <h2>By year</h2>
                            <span className="n">Photos and shares count toward the year their event ran.</span>
                        </div>
                        <TCard>
                            {years.length === 0 ? (
                                <TEmpty message="Nothing yet — numbers appear as events run." />
                            ) : (
                                <div className="org-years" role="table" aria-label="Totals by year">
                                    <div className="yr hd" role="row">
                                        <span role="columnheader">Year</span>
                                        <span role="columnheader">Activations</span>
                                        <span role="columnheader">Photos</span>
                                        <span role="columnheader">Shares</span>
                                    </div>
                                    {years.map((y) => (
                                        <div className={y === thisYear ? 'yr now' : 'yr'} role="row" key={y}>
                                            <span role="cell">{y}</span>
                                            <span role="cell">{Number(countFor(stats.activationsByYear, y)).toLocaleString()}</span>
                                            <span role="cell">{Number(countFor(stats.mediaByYear, y)).toLocaleString()}</span>
                                            <span role="cell">{Number(countFor(stats.sharesByYear, y)).toLocaleString()}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TCard>
                    </>
                )}
            </div>
        </AreaShell>
    );
}
